/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React from 'react';
import { C, TONES, Tone } from '../paleta';

/**
 * Nó do board de Processos — espelho 1:1 do card de nó do protótipo
 * (`sistema/index.html` linhas 135-146 do template + método `node()` linha 1890).
 * O mesmo markup serve macro (grupo), board (bot) e nano (etapa), como no original.
 */

/**
 * Bolinha clicável ACIMA do círculo principal.
 *
 * Nasceu no nano (08/09) com a CONTAGEM de quem já passou pela etapa. Desde 17/09
 * ela serve também macro e board com a PORCENTAGEM de cobertura do nó — o pedido
 * do dono: "para cada nó apresentado nas visualizações, é preciso ter uma
 * bolhazinha acima do nó informando a porcentagem de empresas total que passaram
 * por aquele nó".
 *
 * `title` NUNCA é opcional na prática para o uso de porcentagem: os três níveis
 * têm denominadores diferentes, e é o tooltip que diz qual é o daquele número.
 * `variante='pct'` só muda a largura (um "100%" não cabe em 22px).
 */
export interface BolinhaAcima {
  texto: string;
  title?: string;
  onSelect: () => void;
  /** 'contagem' (padrão, círculo de 22px) | 'pct' (pílula, cabe "100%"). */
  formato?: 'contagem' | 'pct';
  /**
   * QUANTAS empresas passaram por este nó (25/09) — o número ao lado do
   * percentual, pedido do dono.
   *
   * O percentual sozinho é ambíguo entre nós de tamanhos diferentes: "50%" pode
   * ser 1 de 2 ou 300 de 600, e a decisão de onde olhar primeiro depende de
   * qual dos dois é. O denominador já está no tooltip desde 17/09; o que
   * faltava era o NUMERADOR visível sem hover.
   *
   * `null` quando não há número a mostrar (bolha de "—", "·", "⊘"): nesses
   * estados não houve passagem, e um "0" ao lado pareceria uma medição em vez
   * de uma ausência.
   */
  quantidade?: number | null;
  /**
   * O DENOMINADOR do nó, que completa a frase: `93% · 329 de 347 empresas`
   * (25/09, pedido do dono: "igual assim, só que em cima de cada nó também").
   *
   * É o total que ESTE nó processa, não o da área — decisão do dono quando
   * perguntado. O total saiu do rodapé (`emojiMeta` deixou de recebê-lo) e subiu
   * para cá: a bolha passa a ser o único lugar que responde "de quantas?", em
   * vez de o mesmo número aparecer em duas alturas do mesmo nó.
   *
   * Consequência que vale saber: os percentuais dos nós de uma linha deixam de
   * ser comparáveis entre si, porque cada um passa a exibir a sua própria base.
   * O tooltip continua dizendo qual denominador é aquele.
   *
   * `null`/ausente mantém a bolha curta (`93% · 329`), que é o formato de quem
   * ainda não tem total conhecido.
   */
  total?: number | null;
}

export interface NoOpts {
  circleText: string;
  tone: Tone;
  tag: string;
  label: string;
  /** Tooltip do nó — usado para mostrar o NOME REAL quando o label é um apelido. */
  labelTitle?: string;
  barPct: number;
  barErrPct?: number;
  barColor?: string;
  /**
   * Fatia AMBAR da barra: as empresas em ALERTA (05/10), so' no BOARD/NANO.
   *
   * Na MACRO o alerta nao tem fatia propria porque la' ele esta' somado dentro do
   * `barErrPct` — e' a mesma fusao `err + warn` que o rodape da macro ja' faz no
   * `✕`. No board o `⚠` tem numero proprio no rodape, entao a barra tambem o
   * separa: cada nivel pinta exatamente o que ele escreve.
   *
   * Ate' 04/10 o alerta era contado como AVANCO (verde) nos dois niveis, e era o
   * que fazia "Apuracao LP" aparecer 68% verde com `✕ 123` escrito embaixo.
   */
  /** "N nao chegaram a esta etapa" (07/10). `null` = cobertura nao responde. */
  naoChegaram?: NaoChegaramNoNo | null;
  barWarnPct?: number;
  /**
   * Fatia AZUL da barra: as empresas PENDENTES (25/09) — o `skip`, que o bot
   * decidiu nao processar agora (sem movimento, sem tributo).
   *
   * Azul e nao verde/vermelho porque pendente nao e' nenhum dos dois: nada foi
   * produzido (nao e' sucesso) e nada quebrou (nao e' falha). Sem a fatia
   * propria elas ficavam no espaco CINZA da barra, indistinguiveis de quem
   * ainda nao rodou — e sao coisas diferentes: uma o bot ja' decidiu, a outra
   * nao chegou nela.
   */
  barSkipPct?: number;
  meta: string;
  /**
   * Percentual de SUCESSO do no' (30/09, pedido do dono), exibido logo ACIMA da
   * barra: `concluidas / (concluidas + falhas)`.
   *
   * Vem calculado por `pctSucesso`, que documenta a regra. `null`/ausente
   * quando ninguem teve desfecho ainda — ai' a linha nao e' renderizada, em vez
   * de imprimir "0% de sucesso" onde nao houve nem tentativa concluida.
   */
  pctSucesso?: number | null;
  selected?: boolean;
  onSelect: () => void;
  acima?: BolinhaAcima | null;
  /** Selo discreto ao lado do rótulo — "3 RPAs" (13/10). */
  selo?: string | null;
  seloTitle?: string;
  /**
   * PARCELAS DO PAINEL (13/10): ok, erro, não constam, sem movimento, funil.
   * Cada uma com número e percentual, e clicável quando há lista para abrir.
   * Com elas presentes o nó vira cartão e o rodapé `meta` não é renderizado —
   * as parcelas dizem a mesma coisa com percentual.
   */
  parcelas?: ParcelaDoNo[] | null;
}

export interface ParcelaDoNo {
  chave: string;
  icone: string;
  rotulo: string;
  valor: string;
  /** Percentual já formatado ("55%") ou vazio. */
  pct?: string;
  /** Segunda linha, menor, sob o rótulo — a quebra ok/erro/sem desfecho do grupo. */
  detalhe?: string;
  /**
   * Linha COMPACTA (14/10, pedido do dono: "está ocupando duas linhas... diminui
   * pra caber em uma só"): rótulo e `detalhe` lado a lado, em fonte menor, sem
   * quebra. O texto completo fica no `title`.
   */
  compacto?: boolean;
  cor: string;
  title?: string;
  onSelect?: () => void;
}

export interface NoDados {
  circleText: string;
  circleBg: string;
  circleFg: string;
  circleBorder: string;
  tag: string;
  tagColor: string;
  label: string;
  labelTitle?: string;
  barPct: number;
  barErrPct: number;
  barColor: string;
  barWarnPct: number;
  barSkipPct: number;
  meta: string;
  pctSucesso: number | null;
  selBg: string;
  selBorder: string;
  onSelect: () => void;
  acima: BolinhaAcima | null;
  naoChegaram: NaoChegaramNoNo | null;
  selo: string | null;
  seloTitle?: string;
  parcelas: ParcelaDoNo[] | null;
}

/**
 * VIDRO (13/10, pedido do dono: "coloca um liquid glass, deixa mais premium").
 * Exportado para o popup e as seções da aba usarem a MESMA superfície.
 */
export const VIDRO = {
  background: 'linear-gradient(145deg, rgba(255,255,255,0.78), rgba(255,255,255,0.52))',
  backdropFilter: 'blur(18px) saturate(170%)',
  WebkitBackdropFilter: 'blur(18px) saturate(170%)',
  border: '1px solid rgba(255,255,255,0.85)',
  boxShadow:
    '0 10px 30px rgba(16, 60, 48, 0.10), 0 1px 2px rgba(16, 60, 48, 0.06), inset 0 1px 0 rgba(255,255,255,0.9)',
} as const;

/**
 * "N NÃO RODARAM" (09/10, régua do dono) — o indicador abaixo do rodapé do nó.
 *
 * > *"o V é pras que concluíram, o X pras que deram erro e o L (não chegaram) é
 * > apenas pras que não rodaram no agrupamento com base no total de empresas do
 * > universo (universo de empresas daquele departamento de bots)"*
 *
 * > *"temos 100 empresas iniciais no primeiro agrupamento, só que quando eu
 * > chego no último rodaram apenas 40-60, e eu não faço ideia o que aconteceu
 * > com o restante"*
 *
 * SEPARADO da tag "N empresas travadas", que conta `err` DENTRO do nó. Este
 * conta quem está na base da comparação (o fluxo, na macro; o bot anterior, no
 * micro) e NÃO tem log aqui — a empresa que sumiu entre as etapas, que a tag
 * nunca mostrou porque ela não falhou em lugar nenhum: simplesmente não veio.
 *
 * `null` quando a cobertura não permite responder: omitir é melhor que exibir
 * zero, que afirmaria "todas chegaram".
 */
export interface NaoChegaramNoNo {
  /** Total ausente (não processadas aqui + chegaram e não concluíram). */
  total: number;
  /** Não foi processada nesta etapa — não chegou. */
  semLog: number;
  /** Tem log aqui, mas não concluiu. */
  naoConcluiram: number;
  /** Sobre o quê o número é: "empresas do fluxo" ou "concluíram em BOT_X". */
  baseLabel: string;
  /** Derivado de amostra truncada — a tela avisa em vez de mentir por omissão. */
  truncado: boolean;
}

/** Tradução literal do `node()` do protótipo — cores derivadas do tone. */
export const montarNo = (opts: NoOpts): NoDados => {
  const t = TONES[opts.tone];
  let tagColor = 'oklch(0.6 0.015 165)';
  if (opts.tone === 'err') tagColor = C.redFg;
  else if (opts.tone === 'warn') tagColor = C.amberFg;
  return {
    circleText: opts.circleText,
    circleBg: opts.tone === 'ok' ? C.greenBg : t.bg,
    circleFg: t.fg,
    circleBorder: opts.selected ? t.dot : 'transparent',
    tag: opts.tag,
    tagColor,
    label: opts.label,
    labelTitle: opts.labelTitle,
    barPct: opts.barPct,
    barErrPct: opts.barErrPct || 0,
    barColor: opts.barColor || t.dot,
    barWarnPct: opts.barWarnPct || 0,
    barSkipPct: opts.barSkipPct || 0,
    meta: opts.meta,
    pctSucesso: opts.pctSucesso ?? null,
    selBg: opts.selected ? 'oklch(0.975 0.008 165)' : 'transparent',
    selBorder: opts.selected ? 'oklch(0.88 0.03 165)' : 'transparent',
    onSelect: opts.onSelect,
    acima: opts.acima ?? null,
    naoChegaram: opts.naoChegaram ?? null,
    selo: opts.selo ?? null,
    seloTitle: opts.seloTitle,
    parcelas: opts.parcelas?.length ? opts.parcelas : null,
  };
};

interface NoBoardProps {
  dados: NoDados;
  /**
   * Variante das linhas do board (macro/board): nó com max-width 220px e meta com
   * altura mínima fixa (min-height 27px / line-height 13.5px) — exatamente as duas
   * diferenças entre o template de `lineRows` e o de `tlNodes` no protótipo.
   *
   * No NANO todo nó reserva o slot da bolinha "já passaram" ACIMA do círculo
   * (mesmo sem `acima`, para as colunas alinharem) — o centro do círculo desce
   * 30px (22px do slot + 8px do gap): a régua do nano usa top 66px, não 36px.
   *
   * Desde 17/09 a variante 'linha' TAMBÉM reserva o slot, porque macro e board
   * passaram a exibir a bolha de porcentagem. O slot é reservado mesmo sem
   * `acima` (nó sem cobertura conhecida) — senão os nós da mesma linha ficariam
   * com os círculos em alturas diferentes.
   */
  variante: 'linha' | 'nano';
}

/**
 * A bolha acima do círculo. Verde quando há passagem, cinza quando é zero/vazio —
 * a mesma leitura visual nos dois formatos, para "0" e "0%" não parecerem estados
 * diferentes do que são.
 */
const Bolha: React.FC<{ acima: BolinhaAcima }> = ({ acima }) => {
  const ehPct = acima.formato === 'pct';
  // Cinza para tudo que NÃO é afirmação de passagem:
  //   "—"  percentual indisponível (espelho fora)
  //   "·"  não houve execução no período       (21/09)
  //   "⊘"  este bot não reporta etapas          (22/09)
  // Os dois últimos entraram depois e ficavam com a cor de valor positivo —
  // um estado de ausência pintado como conquista, que é o oposto do que dizem.
  const vazio =
    acima.texto === '0' ||
    acima.texto === '0%' ||
    acima.texto === '—' ||
    acima.texto === '·' ||
    acima.texto === '⊘';
  return (
    <div
      onClick={ev => {
        ev.stopPropagation();
        acima.onSelect();
      }}
      title={acima.title}
      style={{
        ...(ehPct ? { minWidth: '34px', padding: '0 6px' } : { width: '22px' }),
        height: '22px',
        borderRadius: ehPct ? '11px' : '50%',
        background: vazio ? 'oklch(0.96 0.006 160)' : C.greenBg,
        color: vazio ? 'oklch(0.55 0.015 165)' : C.greenFg,
        border: `1px solid ${vazio ? 'oklch(0.88 0.008 160)' : C.green}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: '10px',
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: '0 0 0 4px oklch(1 0 0)',
        whiteSpace: 'nowrap',
      }}
    >
      {acima.texto}
      {/* A contagem ao lado do percentual (25/09). Só aparece no formato `pct` e
          só quando há passagem: numa bolha de "—"/"·"/"⊘" o número seria a
          resposta de uma pergunta que a bolha acabou de dizer que não sabe.

          O `de N empresas` completa a frase com o denominador do nó, que saiu do
          rodapé no mesmo pedido. Sem `total` a bolha fica curta (`93% · 329`) em
          vez de inventar uma base. */}
      {ehPct && !vazio && acima.quantidade != null && (
        <span style={{ opacity: 0.72, fontWeight: 500, marginLeft: '4px' }}>
          · {acima.quantidade}
          {/* "concluíram", não "empresas" (05/10): o numerador conta quem teve
              evento `concluido`, e quem só falhou fica de fora. Dizer só
              "248 de 349 empresas" fazia o usuário somar o rodapé (que conta
              TODOS os desfechos) e achar que a tela se contradizia. */}
          {acima.total != null && ` de ${acima.total} concluíram`}
        </span>
      )}
    </div>
  );
};

/**
 * CARTÃO DE ETAPA (13/10) — o nó das linhas macro/board quando há painel.
 *
 * Pedido do dono: *"tá tudo muito apertado... organiza melhor visualmente,
 * coloca um liquid glass, deixa mais premium e se adaptando ao monitor"*. O nó
 * de 220px com seis números em fonte 10px virou um cartão que respira: círculo
 * com o percentual, nome, selo de RPAs, barra e uma linha por parcela — cada
 * linha com número, percentual e clique próprio para abrir aquela lista.
 */
/** Vão entre dois cartões da mesma linha — é o comprimento do fio que os liga. */
export const VAO_ENTRE_CARTOES = 34;

const CartaoEtapa: React.FC<{ n: NoDados; conectado?: boolean; numero?: string }> = ({
  n,
  conectado,
  numero,
}) => (
  <div
    data-tour="cartao"
    onClick={n.onSelect}
    title={n.labelTitle}
    style={{
      ...VIDRO,
      position: 'relative',
      minWidth: 0,
      height: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      padding: '16px 18px 16px',
      borderRadius: '20px',
      cursor: 'pointer',
    }}
  >
    {/* O FIO (13/10, pedido do dono: "faz um fio ligando os cards... para dar uma
        lembrança de linha do tempo"): sai do cartão anterior e chega no círculo
        deste, com a seta no meio do vão. */}
    {conectado && (
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: `-${VAO_ENTRE_CARTOES + 1}px`,
          top: '74px',
          width: `${VAO_ENTRE_CARTOES + 1}px`,
          height: '2px',
          background: 'linear-gradient(90deg, oklch(0.78 0.09 162), oklch(0.62 0.12 162))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          style={{
            width: '18px',
            height: '18px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.98)',
            border: '1.5px solid oklch(0.66 0.11 162)',
            color: 'oklch(0.45 0.1 162)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          ›
        </span>
      </div>
    )}
    <div
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: '10px',
        fontWeight: 600,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: 'oklch(0.5 0.06 165)',
        height: '14px',
      }}
    >
      {numero ?? ''}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
      <div
        data-tour="circulo"
        style={{
          width: '58px',
          height: '58px',
          borderRadius: '50%',
          background: n.circleBg,
          color: n.circleFg,
          border: `2px solid ${n.barColor}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // "99,52%" (duas casas, 14/10) não cabe na fonte do "✓".
          fontSize: n.circleText.length > 3 ? '12.5px' : '17px',
          letterSpacing: n.circleText.length > 3 ? '-0.02em' : undefined,
          fontWeight: 700,
          flex: 'none',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 12px rgba(16, 60, 48, 0.10)',
        }}
      >
        {n.circleText}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }}>
        <div style={{ fontSize: '14.5px', fontWeight: 650, lineHeight: 1.25, textWrap: 'pretty' }}>
          {n.label}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          {n.selo && (
            <span
              title={n.seloTitle}
              style={{
                padding: '2px 8px',
                borderRadius: '20px',
                background: 'rgba(255,255,255,0.75)',
                border: '1px solid oklch(0.9 0.02 162)',
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '10.5px',
                fontWeight: 600,
                color: 'oklch(0.42 0.05 165)',
                whiteSpace: 'nowrap',
              }}
            >
              {n.selo}
            </span>
          )}
          <span style={{ fontSize: '11px', color: n.tagColor }}>{n.tag}</span>
        </div>
      </div>
    </div>
    <div
      style={{
        width: '100%',
        height: '7px',
        borderRadius: '6px',
        background: 'rgba(16, 60, 48, 0.08)',
        overflow: 'hidden',
        display: 'flex',
      }}
    >
      <div style={{ width: `${n.barPct}%`, background: C.green, flex: 'none' }} />
      <div style={{ width: `${n.barErrPct}%`, background: 'oklch(0.58 0.18 28)', flex: 'none' }} />
      <div style={{ width: `${n.barWarnPct}%`, background: C.amber, flex: 'none' }} />
      <div
        style={{ width: `${n.barSkipPct}%`, background: 'oklch(0.72 0.02 165)', flex: 'none' }}
      />
    </div>
    <div data-tour="parcelas" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {(n.parcelas ?? []).map(p =>
        p.compacto ? (
          <div
            key={p.chave}
            title={p.title}
            onClick={
              p.onSelect
                ? ev => {
                    ev.stopPropagation();
                    p.onSelect?.();
                  }
                : undefined
            }
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '7px',
              padding: '3px 8px',
              margin: '0 -8px',
              borderRadius: '8px',
              fontSize: '11.5px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              cursor: p.onSelect ? 'pointer' : 'default',
            }}
          >
            <span style={{ width: '14px', textAlign: 'center', color: p.cor, flex: 'none' }}>
              {p.icone}
            </span>
            <span style={{ fontWeight: 650, color: p.cor, flex: 'none' }}>{p.rotulo}</span>
            <span
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '10.5px',
                color: 'oklch(0.45 0.02 165)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {p.detalhe}
            </span>
          </div>
        ) : (
          <div
            key={p.chave}
            title={p.title}
            onClick={
              p.onSelect
                ? ev => {
                    ev.stopPropagation();
                    p.onSelect?.();
                  }
                : undefined
            }
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '8px',
              padding: '5px 8px',
              margin: '0 -8px',
              borderRadius: '9px',
              fontSize: '12.5px',
              cursor: p.onSelect ? 'pointer' : 'default',
            }}
          >
            <span style={{ width: '14px', textAlign: 'center', color: p.cor, flex: 'none' }}>
              {p.icone}
            </span>
            <span style={{ flex: 1, minWidth: 0, color: 'oklch(0.38 0.02 165)' }}>
              {p.rotulo}
              {p.detalhe && (
                <span
                  style={{
                    display: 'block',
                    marginTop: '2px',
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: '10.5px',
                    color: 'oklch(0.48 0.02 165)',
                  }}
                >
                  {p.detalhe}
                </span>
              )}
            </span>
            <span
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontWeight: 650,
                color: p.cor,
                whiteSpace: 'nowrap',
              }}
            >
              {p.valor}
            </span>
            <span
              style={{
                width: '38px',
                textAlign: 'right',
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '11.5px',
                color: 'oklch(0.52 0.015 165)',
                flex: 'none',
              }}
            >
              {p.pct ?? ''}
            </span>
          </div>
        ),
      )}
    </div>
  </div>
);

export const NoBoard: React.FC<NoBoardProps & { conectado?: boolean; numero?: string }> = ({
  dados: n,
  variante,
  conectado,
  numero,
}) =>
  variante === 'linha' && n.parcelas ? (
    <CartaoEtapa n={n} conectado={conectado} numero={numero} />
  ) : (
    <div
      onClick={n.onSelect}
      title={n.labelTitle}
      style={{
        flex: 1,
        minWidth: 0,
        ...(variante === 'linha' ? { maxWidth: '220px' } : {}),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        padding: '14px 8px 12px',
        borderRadius: '11px',
        background: n.selBg,
        border: `1px solid ${n.selBorder}`,
        cursor: 'pointer',
        position: 'relative',
      }}
    >
      {/* O slot é reservado nas DUAS variantes (mesmo sem `acima`), para os
        círculos da mesma linha alinharem. */}
      <div
        style={{
          height: '22px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
        }}
      >
        {n.acima && <Bolha acima={n.acima} />}
      </div>
      <div
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background: n.circleBg,
          color: n.circleFg,
          border: `2px solid ${n.circleBorder}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '15px',
          fontWeight: 700,
          boxShadow: '0 0 0 5px oklch(1 0 0)',
          flex: 'none',
        }}
      >
        {n.circleText}
      </div>
      <div
        style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: '9px',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: n.tagColor,
          textAlign: 'center',
        }}
      >
        {n.tag}
      </div>
      <div
        style={{
          fontSize: '12.5px',
          fontWeight: 600,
          textAlign: 'center',
          textWrap: 'pretty',
        }}
      >
        {n.label}
      </div>
      <div
        style={{
          width: '100%',
          marginTop: 'auto',
          paddingTop: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        {/* TAXA DE SUCESSO (30/09) — logo acima da barra, onde o dono pediu.
          Reserva altura mesmo quando ausente (`null`), pelo mesmo motivo do slot
          da bolha: sem isso, um nó sem desfecho encolhe e as barras dos nós da
          mesma linha deixam de alinhar.

          Verde só quando é 100%: abaixo disso o número é informação, não
          conquista, e pintar 60% de verde pediria uma escala de cores que a
          barra logo abaixo já dá melhor. */}
        <div
          style={{
            height: '13px',
            // -4px anula parte do `gap: 8px` do contêiner: a taxa é legenda DA
            // barra e fica colada nela, não a meia distância entre o rótulo e ela.
            marginBottom: '-4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          {n.pctSucesso != null && (
            <span
              title="Empresas concluídas sobre as que já tiveram desfecho (concluídas + falhas). Pendentes, na fila e em execução ficam de fora."
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '10.5px',
                fontWeight: 600,
                color: n.pctSucesso === 100 ? C.greenFg : 'oklch(0.5 0.015 165)',
                whiteSpace: 'nowrap',
              }}
            >
              {n.pctSucesso}% de sucesso
            </span>
          )}
        </div>
        <div
          style={{
            width: '100%',
            height: '4px',
            borderRadius: '3px',
            background: 'oklch(0.95 0.008 160)',
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          <div
            style={{ width: `${n.barPct}%`, height: '4px', background: n.barColor, flex: 'none' }}
          />
          <div
            style={{
              width: `${n.barErrPct}%`,
              height: '4px',
              background: 'oklch(0.58 0.18 28)',
              flex: 'none',
            }}
          />
          {/* ALERTA em ambar (05/10) — so' o board/nano usa, porque so' la' o ⚠ tem
            numero proprio no rodape. Na macro ele ja' esta' dentro do vermelho. */}
          <div
            style={{
              width: `${n.barWarnPct}%`,
              height: '4px',
              background: C.amber,
              flex: 'none',
            }}
          />
          {/* PENDENTES em azul (25/09). Depois da falha: a ordem da barra vai do
            desfecho melhor ao pior e termina no que ainda nao tem desfecho —
            concluido, falhou, alerta, pendente, e o cinza do fundo para quem
            nao rodou. */}
          <div
            style={{
              width: `${n.barSkipPct}%`,
              height: '4px',
              background: 'oklch(0.62 0.13 250)',
              flex: 'none',
            }}
          />
        </div>
        <div
          // O `title` explica o rodapé inteiro, e em especial o `(⏭ N)`: ele é um
          // RECORTE das concluídas, não uma parcela da soma (09/10, pedido do
          // dono: "dê uma explicação sobre o que é o skip").
          title={
            'Desfecho de cada empresa neste agrupamento. ' +
            '✓ concluíram · ✕ erro ou alerta · ' +
            '(⏭ N) = destas, N foram PULADAS pelo bot (nada a fazer: sem ' +
            'movimento, sem tributo ou já transmitida) — elas já estão contadas ' +
            'no ✓, por isso vêm entre parênteses. ' +
            '🚚 rodando agora · ⏳ na fila.'
          }
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: '10.5px',
            color: 'oklch(0.58 0.015 165)',
            textAlign: 'center',
            ...(variante === 'linha' ? { minHeight: '27px', lineHeight: '13.5px' } : {}),
          }}
        >
          {n.meta}
        </div>
        {/* "N não chegaram a esta etapa" (07/10, pedido do dono): quantas da base
          não têm log aqui. É DIFERENTE da tag "N empresas travadas" logo acima,
          que conta `err` dentro do nó — a empresa que sumiu entre as etapas não
          falhou em lugar nenhum, e era justamente ela que a tela não mostrava.

          Omitido (não zerado) quando a cobertura não responde — ver
          `NaoChegaramNoNo`. */}
        {n.naoChegaram && n.naoChegaram.total > 0 && (
          <div
            title={
              `${n.naoChegaram.total} não rodaram neste agrupamento ` +
              `(de ${n.naoChegaram.baseLabel})${n.naoChegaram.truncado ? ' — amostra parcial' : ''}`
            }
            style={{
              marginTop: '3px',
              fontSize: '11px',
              fontWeight: 600,
              color: C.amberFg,
              textAlign: 'center',
            }}
          >
            {/* O `+` cola no NÚMERO ("47+ não chegaram"), não no fim da frase:
              ele qualifica a contagem, e solto no final lia como outra coisa. */}
            {`↳ ${n.naoChegaram.total}${n.naoChegaram.truncado ? '+' : ''} não rodaram`}
          </div>
        )}
      </div>
    </div>
  );
